import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { ApiService } from 'src/app/services/api/api.service';

import { ErrorModalComponent } from 'src/app/shared/modals/error-modal/error-modal.component';
import { SuccessModalComponent } from 'src/app/shared/modals/success-modal/success-modal.component';
import { UtilitiesService } from 'src/app/services/utilities/utilities.service';
import { Router } from '@angular/router';
import {
  handleFileUploaded,
  handleFileUploadError,
} from 'src/app/shared/components/upload-input/file-upload-helper';
import { ConfirmActionComponent } from 'src/app/shared/components/confirm-action/confirm-action.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PaymentModalComponent } from 'src/app/shared/modals/payment-modal/payment-modal.component';
import { Location } from '@angular/common';

@Component({
  selector: 'app-lga-identification-application',
  standalone: false,
  templateUrl: './lga-identification-application.component.html',
  styleUrl: './lga-identification-application.component.scss',
})
export class LgaIdentificationApplicationComponent {
  isSubmitting: boolean;
  isVerifyingAsin: boolean;
  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private dialog: MatDialog,
    private utilitiesService: UtilitiesService,
    private router: Router,
    private authService: AuthService,
    private location: Location,
  ) {}

  showErrorMessages: boolean = false;

  form: FormGroup;

  wardList: any;

  ngOnInit(): void {
    this.fetchWards();
    this.initializeForm();
  }

  fetchWards() {
    const lgaId = this.authService.getUser()?.lgaId;

    if (lgaId) {
      this.apiService.get('/utility/all/ward/' + lgaId).subscribe(
        (response) => {
          this.wardList = response;
        },
        (error) => {
          console.error('Error Fetching Local Government: ', error);
        },
      );
    }
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      compoundName: ['', [Validators.required]],
      ward: [null, [Validators.required]],
      communityApproval: [null, Validators.required],
      lastName: ['', [Validators.required, Validators.maxLength(60)]],
      firstName: ['', [Validators.required, Validators.maxLength(60)]],
      age: [null, [Validators.required]],
      middleName: ['', [Validators.maxLength(60)]],
      phoneNumber: [
        '',
        [Validators.required, this.phoneNumberValidator() as ValidatorFn],
      ],
      email: ['', [Validators.maxLength(60), this.emailValidator()]],
      street: ['', [Validators.required, Validators.maxLength(120)]],
      iAgreeToTerms: [false, Validators.requiredTrue],

      laisonLastName: ['', [Validators.required, Validators.maxLength(60)]],
      laisonFirstName: ['', [Validators.required, Validators.maxLength(60)]],
      laisonMiddleName: ['', [Validators.maxLength(60)]],
      laisonPosition: ['', [Validators.required, Validators.maxLength(70)]],
      laisonPhoneNumber: [
        '',
        [Validators.required, this.phoneNumberValidator() as ValidatorFn],
      ],
      laisonEmail: ['', [Validators.maxLength(60), this.emailValidator()]],
      certificatePurpose: [
        '',
        [Validators.required, Validators.maxLength(300)],
      ],
    });
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || control.value === undefined) {
        return null;
      }

      const cleanedPhoneNumber = control.value
        .replace(/\s/g, '')
        .replace(/-/g, '');

      const isDigitsOnly = /^\d+$/.test(cleanedPhoneNumber);

      if (
        cleanedPhoneNumber.length === 0 ||
        (cleanedPhoneNumber.length === 11 && isDigitsOnly) ||
        (cleanedPhoneNumber.length === 13 &&
          cleanedPhoneNumber.startsWith('234')) ||
        (cleanedPhoneNumber.length === 14 &&
          cleanedPhoneNumber.startsWith('+234'))
      ) {
        return null;
      } else {
        return { invalidPhoneNumber: true };
      }
    };
  }

  emailValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

      if (control.value && !emailPattern.test(control.value)) {
        return { invalidEmail: true };
      }

      return null;
    };
  }

  onFileChange(
    fileDetails: { file: File; readableUrl: string } | null,
    controlName: string,
  ) {
    handleFileUploaded(fileDetails, this.form, controlName);
  }

  onError(errorResponse: any, controlName: string) {
    handleFileUploadError(errorResponse, this.form, controlName);
  }

  verifyAsin() {
    const asin = this.form.get('asin')?.value;

    this.isVerifyingAsin = true;

    this.apiService
      .get('/utility/asin/validate?tin=' + asin)
      .pipe(
        finalize(() => {
          this.isVerifyingAsin = false;
        }),
      )
      .subscribe(
        (response: any) => {
          this.form.patchValue({
            businessName: response?.firstName,
          });

          this.openAsinVerificationSuccessModal('ASIN verified successfuly');
        },
        (error) => {
          this.openErrorModal(error?.message);
        },
      );
  }

  openSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.router.navigate(['/admin/lga-identification']);
    });
  }

  openErrorModal(message: string) {
    let dialogRef = this.dialog.open(ErrorModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { errorMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  openConfirmModal() {
    this.dialog.open(ConfirmActionComponent, {
      width: '400px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: [
        'modal-form',
        'operation-modal',
        'modal-no-overflow-changed',
      ],
      disableClose: true,

      data: {
        title: 'Are you sure you want to create this LGA Identification?',
        body: '',
        confirmText: 'Yes',
        lottieIconPath: 'assets/lottie/alert.json',
        onConfirm: () => {
          this.create();
        },
        onCancel: () => {},
        showCancelButton: true,
        showConfirmButton: true,
      },
    });
  }

  async create(): Promise<void> {
    if (this.form.valid) {
      this.isSubmitting = true;

      try {
        const formData = this.form.value;
        const request = await this.mapFormValuesToObject(formData);

        const url = '/lga-identification/apply';

        this.apiService
          .post(url, request)
          .pipe(finalize(() => (this.isSubmitting = false)))
          .subscribe(
            (res) => {
              if (res.success) {
                this.openSuccessModal(
                  res.message || 'LGA identification application successful',
                );
              } else {
                this.openErrorModal(res.error || res.message);
              }
            },
            (error) => {
              this.openErrorModal(error?.message);
            },
          );
      } catch (error) {
        this.isSubmitting = false;
        this.openErrorModal('Failed to process form data');
      }
    }
  }

  openPaymentModal(data: any) {
    this.dialog
      .open(PaymentModalComponent, {
        width: '400px',
        backdropClass: 'modal-form-backdrop',
        hasBackdrop: true,
        disableClose: true,
        panelClass: ['modal-form', 'custom-dialog-md'],
        data: {
          paymentTitle: 'Application Fee',
          paymentAmount: 4000,
        },
      })
      .afterClosed()
      .subscribe((res: any) => {
        if (res.reload) {
        }
      });
  }

  async mapFormValuesToObject(formValue: any): Promise<any> {
    let approvalLetterBase64 = '';

    if (formValue.communityApproval?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.communityApproval.file,
      );
      approvalLetterBase64 = fullBase64.split(',')[1];
    }

    return {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      middleName: formValue.middleName,
      phoneNumber: formValue.phoneNumber,
      email: formValue.email,
      age: formValue.age,
      street: formValue.street,
      compoundName: formValue.compoundName,
      ward: formValue.ward,
      communityApproval: approvalLetterBase64,

      laisonLastName: formValue.laisonLastName,
      laisonFirstName: formValue.laisonFirstName,
      laisonMiddleName: formValue.laisonMiddleName,
      laisonPosition: formValue.laisonPosition,
      laisonPhoneNumber: formValue.laisonPhoneNumber,
      laisonEmail: formValue.laisonEmail,
      certificatePurpose: formValue.certificatePurpose,
    };
  }

  openAsinVerificationSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  back() {
    this.location.back();
  }
}
